import React, { useState, useEffect } from 'react';
import api from '../api/api';
import styles from './MatchingResult.module.css';
import {
  FileText, CheckCircle2, AlertTriangle, XCircle,
  Shield, Target, HelpCircle, TrendingUp, Briefcase,
  Award, Users, MapPin, MessageCircle, Clock,
  Cpu, Activity, Send, Loader2, ChevronRight,
  Star, Zap,
} from 'lucide-react';

const safeParse = (v, fallback = {}) => {
  if (!v) return fallback;
  try { return typeof v === 'string' ? JSON.parse(v) : v; }
  catch { return fallback; }
};

/* ─── Score gauge ─── */
const ScoreGauge = ({ percentage, verdict }) => {
  const r = 45;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percentage / 100) * circ;
  const color = verdict?.includes('NO_GO') ? 'var(--color-error,#ef4444)'
    : verdict?.includes('ALTERNATIVE') ? '#f97316'
    : verdict?.includes('WARNING')     ? 'var(--color-warning,#f59e0b)'
    : 'var(--color-success,#10b981)';

  return (
    <div className={styles.gaugeWrap}>
      <svg width="120" height="120" viewBox="0 0 110 110">
        <circle className={styles.gaugeTrack} strokeWidth="8" r={r} cx="55" cy="55" />
        <circle
          className={styles.gaugeArc}
          strokeWidth="8"
          stroke={color}
          r={r} cx="55" cy="55"
          style={{ strokeDasharray: circ, strokeDashoffset: offset }}
        />
      </svg>
      <div className={styles.gaugeCenter}>
        <span className={styles.gaugePct} style={{ color }}>{percentage}%</span>
        <span className={styles.gaugeCaption}>Score RH</span>
      </div>
    </div>
  );
};

/* ─── Semantic bar ─── */
const SemanticBar = ({ score }) => {
  const grad = score >= 80
    ? 'linear-gradient(90deg,#4facfe,#00f2fe)'
    : score >= 50
    ? 'linear-gradient(90deg,#a18cd1,#fbc2eb)'
    : 'linear-gradient(90deg,#ff9a9e,#fecfef)';

  return (
    <div className={styles.semanticCard}>
      <div className={styles.semanticTop}>
        <div className={styles.semanticLabel}>
          <Cpu size={13} strokeWidth={2} className={styles.aiIcon} />
          Alignement Sémantique
        </div>
        <strong className={styles.semanticScore}>{score}%</strong>
      </div>
      <div className={styles.semanticTrack}>
        <div className={styles.semanticFill} style={{ width: `${score}%`, background: grad }} />
      </div>
      <p className={styles.semanticHint}>Analyse vectorielle (Embeddings)</p>
    </div>
  );
};

/* ─── Pillar card ─── */
const PillarCard = ({ name, score, max, icon }) => {
  const pct = (score / max) * 100;
  const color = pct >= 75 ? 'var(--color-success,#10b981)'
    : pct >= 50 ? 'var(--color-warning,#f59e0b)'
    : 'var(--color-error,#ef4444)';
  return (
    <div className={styles.pillarCard}>
      <div className={styles.pillarHeader}>
        <span className={styles.pillarIcon}>{icon}</span>
        <span className={styles.pillarName}>{name}</span>
      </div>
      <div className={styles.pillarScoreRow}>
        <span className={styles.pillarVal}>{score}</span>
        <span className={styles.pillarMax}>/{max}</span>
      </div>
      <div className={styles.pillarTrack}>
        <div className={styles.pillarFill} style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MATCHING RESULT — COMPOSANT PRINCIPAL
   ════════════════════════════════════════════════════════════ */
const MatchingResult = ({ data }) => {
  const [scoringDetails, setScoringDetails] = useState(null);
  const [risks, setRisks]         = useState(null);
  const [sales, setSales]         = useState(null);
  const [interview, setInterview] = useState(null);
  const [recruiterTips, setRecruiterTips]   = useState(null);
  const [aiTransparency, setAiTransparency] = useState(null);
  const [isSendingEmail, setIsSendingEmail]   = useState(false);
  const [isWhatsappLoading, setIsWhatsappLoading] = useState(false);

  useEffect(() => {
    if (!data) return;
    setScoringDetails(safeParse(data.scoring_details, {}));
    setRisks(safeParse(data.risks_json, {}));
    setSales(safeParse(data.sales_json, {}));
    setInterview(safeParse(data.interview_json, {}));
    setRecruiterTips(safeParse(data.recruiter_tips_json, {}));
    setAiTransparency(safeParse(data.ai_transparency_json, {}));
  }, [data]);

  if (!data) return null;

  const recordId = data?.id || data?.task_id;

  const handleSendEmail = async () => {
    if (!recordId) return alert('Identifiant introuvable.');
    const email = window.prompt("Email du client :");
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return alert('Email invalide.');
    setIsSendingEmail(true);
    try {
      await api.post(`/matching/history/${recordId}/send-email`, { email });
      alert(`Envoyé à ${email} !`);
    } catch (err) {
      alert(err.response?.data?.error || "Erreur d'envoi.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!recordId) return alert('Identifiant introuvable.');
    setIsWhatsappLoading(true);
    try {
      const { data: res } = await api.get(`/matching/history/${recordId}/whatsapp-pitch`);
      if (navigator.share && navigator.canShare) {
        try { await navigator.share({ text: res.message }); setIsWhatsappLoading(false); return; }
        catch { /* fallback */ }
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(res.message)}`, '_blank');
    } catch {
      alert('Impossible de générer le message.');
    } finally {
      setIsWhatsappLoading(false);
    }
  };

  const verdictClass = (() => {
    if (!data.verdict) return styles.verdictGreen;
    const v = data.verdict.toUpperCase();
    if (v.includes('NO_GO'))      return styles.verdictRed;
    if (v.includes('ALTERNATIVE')) return styles.verdictOrange;
    if (v.includes('WARNING'))    return styles.verdictYellow;
    return styles.verdictGreen;
  })();

  const pillars = [
    { name: 'Compétences Techniques', score: data.score_technical    || 0, max: 25, icon: <Target     size={14} strokeWidth={2} /> },
    { name: 'Expertise Sectorielle',  score: data.score_sector       || 0, max: 20, icon: <Briefcase  size={14} strokeWidth={2} /> },
    { name: 'Certifications',         score: data.score_certifications || 0, max: 15, icon: <Award    size={14} strokeWidth={2} /> },
    { name: 'Envergure Clients',      score: data.score_client_caliber || 0, max: 12, icon: <Users    size={14} strokeWidth={2} /> },
    { name: 'Stabilité',              score: data.score_stability    || 0, max: 10, icon: <TrendingUp size={14} strokeWidth={2} /> },
    { name: 'Géographie',             score: data.score_geography    || 0, max: 8,  icon: <MapPin     size={14} strokeWidth={2} /> },
    { name: 'Soft Skills',            score: data.score_soft_skills  || 0, max: 6,  icon: <MessageCircle size={14} strokeWidth={2} /> },
    { name: 'Disponibilité',          score: data.score_availability || 0, max: 4,  icon: <Clock      size={14} strokeWidth={2} /> },
  ];

  const strengths = safeParse(data.strengths, []);
  const concerns  = safeParse(data.concerns, []);

  return (
    <div className={styles.resultCard}>

      {/* ── Actions rapides ── */}
      {recordId && (
        <div className={styles.actionBar}>
          <button className={styles.actionBtnEmail} onClick={handleSendEmail} disabled={isSendingEmail}>
            {isSendingEmail
              ? <><Loader2 size={15} strokeWidth={2} className={styles.spin} />Envoi…</>
              : <><Send size={15} strokeWidth={2} />Envoyer par email</>}
          </button>
          <button className={styles.actionBtnWa} onClick={handleWhatsApp} disabled={isWhatsappLoading}>
            {isWhatsappLoading
              ? <><Loader2 size={15} strokeWidth={2} className={styles.spin} />Génération…</>
              : <><MessageCircle size={15} strokeWidth={2} />WhatsApp Pitch</>}
          </button>
        </div>
      )}

      {/* ── 1. Header Candidat vs Poste ── */}
      <div className={styles.fileHeader}>
        <div className={styles.fileBlock}>
          <div className={styles.fileIconWrap}><FileText size={20} strokeWidth={1.75} /></div>
          <div>
            <strong>{data.candidate_name || data.file1_filename || 'Candidat'}</strong>
            {data.job_title && <p className={styles.fileSub}>{data.job_title}</p>}
          </div>
        </div>
        <div className={styles.vsPill}>VS</div>
        <div className={`${styles.fileBlock} ${styles.fileBlockRight}`}>
          <div className={styles.fileIconWrap}><Briefcase size={20} strokeWidth={1.75} /></div>
          <div className={styles.fileBlockText}>
            <strong>{data.client_name || data.file2_filename || 'Client'}</strong>
            {data.client_sector && <p className={styles.fileSub}>{data.client_sector}</p>}
          </div>
        </div>
      </div>

      {/* ── 2. Executive Summary ── */}
      <div className={styles.summaryGrid}>
        {/* Colonne scores */}
        <div className={styles.scoresCol}>
          <ScoreGauge percentage={data.global_score || 0} verdict={data.verdict} />
          {data.semantic_score !== undefined && <SemanticBar score={data.semantic_score} />}
          {data.verdict_icon && <div className={styles.verdictEmoji}>{data.verdict_icon}</div>}
        </div>

        {/* Colonne texte */}
        <div className={styles.summaryCol}>
          <div className={styles.summaryTop}>
            <h3 className={styles.summaryTitle}>Synthèse de l'analyse</h3>
            {data.verdict && (
              <span className={`${styles.verdictBadge} ${verdictClass}`}>
                {data.verdict.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          {data.one_liner && (
            <div className={styles.oneLiner}>
              <Activity size={14} strokeWidth={2} />
              {data.one_liner}
            </div>
          )}

          <p className={styles.reasoning}>{data.summary || 'Analyse en cours…'}</p>

          <div className={styles.swot}>
            {strengths.length > 0 && (
              <div className={styles.strengthsBox}>
                <h5><CheckCircle2 size={13} strokeWidth={2.5} /> Forces Clés</h5>
                <ul>{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {concerns.length > 0 && (
              <div className={styles.concernsBox}>
                <h5><AlertTriangle size={13} strokeWidth={2.5} /> Points de Vigilance</h5>
                <ul>{concerns.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 3. Piliers ── */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <Target size={16} strokeWidth={2} /> Analyse par Piliers
        </h4>
        <div className={styles.pillarsGrid}>
          {pillars.map((p, i) => <PillarCard key={i} {...p} />)}
        </div>
      </div>

      {/* ── 4. Compétences Techniques ── */}
      {scoringDetails?.technical_skills && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <Target size={16} strokeWidth={2} /> Compétences Techniques
          </h4>

          {scoringDetails.technical_skills.must_have?.length > 0 && (
            <div className={styles.tableWrap}>
              <p className={styles.tableCaption}>Must-Have</p>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Compétence</th><th>Statut</th><th>Niveau</th><th>Preuve CV</th>
                  </tr>
                </thead>
                <tbody>
                  {scoringDetails.technical_skills.must_have.map((sk, i) => (
                    <tr key={i}>
                      <td className={styles.tdBold}>{sk.skill}</td>
                      <td>
                        <span className={`${styles.statusBadge}
                          ${sk.status === 'Présent'      ? styles.statusGreen
                          : sk.status === 'Transférable' ? styles.statusYellow
                          : styles.statusRed}`}>
                          {sk.status}
                        </span>
                      </td>
                      <td>{sk.level}</td>
                      <td className={styles.tdMuted}>{sk.evidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.techExtras}>
            {scoringDetails.technical_skills.nice_to_have?.length > 0 && (
              <div className={styles.extrasBox}>
                <h5>Bonus (Nice-to-Have)</h5>
                <div className={styles.tagCloud}>
                  {scoringDetails.technical_skills.nice_to_have.map((sk, i) => (
                    <span key={i} className={styles.skillTag}>
                      <CheckCircle2 size={11} strokeWidth={2.5} /> {sk}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {scoringDetails.technical_skills.gaps?.length > 0 && (
              <div className={`${styles.extrasBox} ${styles.gapsBox}`}>
                <h5><XCircle size={13} strokeWidth={2} /> Manques Critiques</h5>
                <ul>{scoringDetails.technical_skills.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 5. Risques & Vente ── */}
      <div className={styles.dualSection}>
        {risks && Object.keys(risks).length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}><Shield size={16} strokeWidth={2} /> Matrice de Risques</h4>
            <div className={styles.riskList}>
              {[
                { label: 'Risque Global', value: risks.overall },
                { label: 'Flight Risk',   value: risks.flight_risk },
                { label: 'Ramp-up',       value: risks.ramp_up },
              ].map(({ label, value }) => (
                <div key={label} className={styles.riskRow}>
                  <span className={styles.riskLabel}>{label}</span>
                  <span className={`${styles.riskBadge}
                    ${value === 'LOW'  ? styles.riskLow
                    : value === 'HIGH' ? styles.riskHigh
                    : styles.riskMed}`}>
                    {value || '–'}
                  </span>
                </div>
              ))}
              {risks.ramp_up_details && (
                <p className={styles.riskNote}>{risks.ramp_up_details}</p>
              )}
            </div>
          </div>
        )}

        {sales && Object.keys(sales).length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}><Briefcase size={16} strokeWidth={2} /> Argumentaire Commercial</h4>
            {sales.positioning && (
              <div className={styles.positioning}>
                <strong>Angle d'attaque</strong>
                <p>{sales.positioning}</p>
              </div>
            )}
            <div className={styles.argList}>
              {sales.killer_arguments?.slice(0, 3).map((arg, i) => (
                <div key={i} className={styles.argItem}>
                  <CheckCircle2 size={15} strokeWidth={2.5} className={styles.argCheck} />
                  <div>
                    <p className={styles.argTitle}>{arg.argument}</p>
                    <p className={styles.argBenefit}>→ {arg.client_benefit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── 6. Entretien ── */}
      {interview?.must_ask?.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}><HelpCircle size={16} strokeWidth={2} /> Questions d'Entretien</h4>
          <div className={styles.interviewList}>
            {interview.must_ask.map((q, i) => (
              <div key={i} className={styles.questionCard}>
                <p className={styles.questionText}>« {q.question} »</p>
                <div className={styles.questionMeta}>
                  <span className={styles.questionWhy}>{q.why}</span>
                  {q.good_answer && (
                    <div className={styles.goodAnswer}>
                      <CheckCircle2 size={12} strokeWidth={2.5} /> {q.good_answer}
                    </div>
                  )}
                  {q.red_flag && (
                    <div className={styles.redFlag}>
                      <AlertTriangle size={12} strokeWidth={2.5} /> {q.red_flag}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 7. Conseils & Transparence ── */}
      <div className={styles.dualSection}>
        {recruiterTips && Object.keys(recruiterTips).length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}><Users size={16} strokeWidth={2} /> Conseils Recruteur</h4>
            <ul className={styles.tipsList}>
              {recruiterTips.next_steps?.map((s, i) => (
                <li key={i}>
                  <ChevronRight size={13} strokeWidth={2.5} className={styles.tipArrow} />
                  {s}
                </li>
              ))}
            </ul>
            {recruiterTips.mistakes_to_avoid?.length > 0 && (
              <div className={styles.mistakesBox}>
                <strong>À éviter :</strong>
                <ul>{recruiterTips.mistakes_to_avoid.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {aiTransparency && Object.keys(aiTransparency).length > 0 && (
          <div className={`${styles.section} ${styles.transparencySection}`}>
            <h4 className={styles.sectionTitle}>
              <Zap size={16} strokeWidth={2} /> Transparence IA
            </h4>
            <div className={styles.confRow}>
              <span>Confiance modèle</span>
              <span className={`${styles.confBadge}
                ${aiTransparency.confidence === 'HIGH' ? styles.confHigh
                : aiTransparency.confidence === 'LOW'  ? styles.confLow
                : styles.confMed}`}>
                {aiTransparency.confidence} — {aiTransparency.confidence_score}%
              </span>
            </div>
            {aiTransparency.missing_info?.length > 0 && (
              <div className={styles.missingBox}>
                <strong>Informations manquantes :</strong>
                <ul>{aiTransparency.missing_info.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingResult;